// purchaseOrderItemsModel.js
//
// Field reads + money rollups for the Purchase Order Items table.

import { parsePriceCents } from 'utilities/Currency';
import { buildNodeFieldPatch, readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getEnumOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { readPurchaseOrderField } from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';
import { sumPaidPurchaseOrderPayments } from 'nodetypes/purchaseorder/view/purchaseOrderPaymentsModel';

const PURCHASE_ORDER_ITEM_TYPE_KEY = 'PURCHASE_ORDER_ITEM';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTypeKey(value) {
  return normalizeString(value).toUpperCase();
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function toMoneyNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function toQuantityNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * @param {object|null|undefined} item
 * @param {string} fieldName
 * @returns {unknown}
 */
export function readPurchaseOrderItemField(item, fieldName) {
  return readNodeFieldRawValue(
    item,
    { name: fieldName, source: 'purchase_order_item' },
    PURCHASE_ORDER_ITEM_TYPE_KEY,
  );
}

/**
 * Line total in cents: qty × unitPriceCents.
 *
 * `unitPrice` is already minor units (cents). Do not multiply by 100 again.
 * Uses parsePriceCents so legacy major-unit floats still normalize correctly.
 *
 * @param {unknown} quantity
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computePurchaseOrderItemLineTotal(quantity, unitPrice) {
  const qty = toQuantityNumber(quantity);
  if (qty == null) return null;
  if (unitPrice === null || unitPrice === undefined || unitPrice === '') return null;
  const priceCents = parsePriceCents(unitPrice);
  if (!Number.isFinite(priceCents)) return null;
  return Math.round(qty * priceCents);
}

/**
 * Resolve line total: prefer qty × unitPrice; fall back to stored `total`.
 *
 * @param {object|null|undefined} item
 * @returns {number|null}
 */
export function resolvePurchaseOrderItemLineTotal(item) {
  const quantity = toQuantityNumber(readPurchaseOrderItemField(item, 'quantity'));
  const unitPrice = toMoneyNumber(readPurchaseOrderItemField(item, 'unitPrice'));
  const derived = computePurchaseOrderItemLineTotal(quantity, unitPrice);
  if (derived != null) return derived;
  return toMoneyNumber(readPurchaseOrderItemField(item, 'total'));
}

/**
 * When quantity or unitPrice changes on a PO item, also write computed `total`
 * into the same patch (same data_* section).
 *
 * @param {object} options
 * @param {object|null|undefined} options.node
 * @param {string} options.nodeTypeKey
 * @param {object|null|undefined} options.field
 * @param {unknown} options.value
 * @param {object|null|undefined} options.patch
 * @returns {object|null|undefined}
 */
export function augmentPurchaseOrderItemTotalPatch({
  node,
  nodeTypeKey,
  field,
  value,
  patch,
}) {
  if (!patch || !node) return patch;
  if (normalizeTypeKey(nodeTypeKey || node?.type) !== PURCHASE_ORDER_ITEM_TYPE_KEY) {
    return patch;
  }

  const fieldName = normalizeString(field?.name);
  if (fieldName !== 'quantity' && fieldName !== 'unitPrice') return patch;

  const nextQuantity = fieldName === 'quantity'
    ? value
    : readPurchaseOrderItemField(node, 'quantity');
  const nextUnitPrice = fieldName === 'unitPrice'
    ? value
    : readPurchaseOrderItemField(node, 'unitPrice');
  const total = computePurchaseOrderItemLineTotal(nextQuantity, nextUnitPrice);
  if (total == null) return patch;

  const totalField = {
    name: 'total',
    source: field?.source || 'purchase_order_item',
    type: 'money',
  };
  const totalPatch = buildNodeFieldPatch(node, totalField, nodeTypeKey, total);
  if (!totalPatch) return patch;

  // Merge only `total` into the primary patch so we don't clobber the qty/price update
  // with a stale section snapshot from buildNodeFieldPatch.
  const merged = { ...patch };
  Object.entries(totalPatch).forEach(([dataKey]) => {
    const existingSection = (
      merged[dataKey] && typeof merged[dataKey] === 'object'
        ? merged[dataKey]
        : {}
    );
    merged[dataKey] = {
      ...existingSection,
      total,
    };
  });
  return merged;
}

/**
 * @param {object|null|undefined} item
 * @param {object|null|undefined} [itemType]
 * @param {string} [language='en']
 * @returns {{
 *   key: string,
 *   title: string,
 *   description: string,
 *   subtitle: string,
 *   leadTime: string,
 *   coverUrl: string,
 *   sku: string,
 *   manufacturer: string,
 *   status: string,
 *   quantity: number|null,
 *   unit: string,
 *   unitLabel: string,
 *   unitPrice: number|null,
 *   total: number|null,
 * }}
 */
export function getPurchaseOrderItemRowModel(item, itemType = null, language = 'en') {
  const unitValue = normalizeString(readPurchaseOrderItemField(item, 'unit'));
  const unitField = Array.isArray(itemType?.schema?.fields)
    ? itemType.schema.fields.find((field) => normalizeString(field?.name) === 'unit')
    : null;
  const unitOptions = unitField ? resolveEnumOptionsForField(unitField, language) : [];
  const unitLabel = unitValue
    ? (getEnumOptionLabel(unitOptions, unitValue, language) || unitValue)
    : '';

  const leadTime = normalizeString(readPurchaseOrderItemField(item, 'leadTime'));
  const description = normalizeString(getNodeSubtitle(item));
  // Prefer description/subtitle field; fall back to lead time for shipping context.
  const subtitle = description || leadTime;

  return {
    key: normalizeString(item?.key),
    title: normalizeString(item?.title),
    description,
    subtitle,
    leadTime,
    coverUrl: normalizeString(item?.coverUrl),
    sku: normalizeString(readPurchaseOrderItemField(item, 'sku')),
    manufacturer: normalizeString(readPurchaseOrderItemField(item, 'manufacturer')),
    status: normalizeString(readPurchaseOrderItemField(item, 'status')),
    quantity: toQuantityNumber(readPurchaseOrderItemField(item, 'quantity')),
    unit: unitValue,
    unitLabel,
    unitPrice: toMoneyNumber(readPurchaseOrderItemField(item, 'unitPrice')),
    total: resolvePurchaseOrderItemLineTotal(item),
  };
}

/**
 * Footer rollups for the Items table.
 * Subtotal comes from lines; tax from the PO header when present.
 * Amount paid prefers paid PURCHASE_ORDER_PAYMENT rows when any payments exist;
 * otherwise falls back to the PO header amountPaid field.
 *
 * @param {object[]} items
 * @param {object|null|undefined} purchaseOrderNode
 * @param {object|null|undefined} [purchaseOrderType]
 * @param {object[]|null} [payments]
 * @returns {{
 *   subTotal: number,
 *   tax: number,
 *   total: number,
 *   amountPaid: number,
 *   balanceDue: number,
 * }}
 */
export function computePurchaseOrderItemsRollups(
  items,
  purchaseOrderNode = null,
  purchaseOrderType = null,
  payments = null,
) {
  const subTotal = (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const lineTotal = resolvePurchaseOrderItemLineTotal(item);
    return sum + (lineTotal == null ? 0 : lineTotal);
  }, 0);

  const tax = toMoneyNumber(readPurchaseOrderField(purchaseOrderNode, 'tax', purchaseOrderType)) || 0;

  let amountPaid;
  if (Array.isArray(payments) && payments.length > 0) {
    amountPaid = sumPaidPurchaseOrderPayments(payments);
  } else {
    amountPaid = toMoneyNumber(
      readPurchaseOrderField(purchaseOrderNode, 'amountPaid', purchaseOrderType),
    ) || 0;
  }

  const total = subTotal + tax;
  const balanceDue = total - amountPaid;

  return {
    subTotal,
    tax,
    total,
    amountPaid,
    balanceDue,
  };
}
